import { supabase } from "@/integrations/supabase/client";
import { haversineKm } from "@/lib/geo";
import type { Category } from "@/lib/categories";

export type Severity = "info" | "warning" | "urgent" | "critical";

export const SEVERITY_META: Record<Severity, { label: string; color: string; badge: string }> = {
  info: { label: "Low", color: "#0EA5E9", badge: "sev-info" },
  warning: { label: "Medium", color: "#F59E0B", badge: "sev-warning" },
  urgent: { label: "High", color: "#EA580C", badge: "sev-urgent" },
  critical: { label: "Critical", color: "#DC2626", badge: "sev-critical" },
};

export const SEVERITY_LIST: Severity[] = ["info", "warning", "urgent", "critical"];

export const REPORT_STATUSES = [
  "open", "under_review", "confirmed", "assigned", "in_progress", "resolved", "closed",
] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

export const STATUS_META: Record<string, { label: string; color: string }> = {
  open: { label: "Reported", color: "#64748B" },
  under_review: { label: "Under review", color: "#8B5CF6" },
  discussing: { label: "Discussing", color: "#8B5CF6" },
  confirmed: { label: "Community verified", color: "#0EA5E9" },
  assigned: { label: "Assigned", color: "#F59E0B" },
  in_progress: { label: "In progress", color: "#F59E0B" },
  resolved: { label: "Resolved", color: "#22C55E" },
  closed: { label: "Closed", color: "#94A3B8" },
};

export type NearbyDuplicate = {
  id: string;
  title: string;
  category: string;
  status: string;
  created_at: string;
  distance_km: number;
  verify_count: number;
};

/** Find recent reports of the same category within `radiusKm` of a point. */
export async function findNearbyDuplicates(opts: {
  lat: number;
  lng: number;
  category: Category | string;
  radiusKm?: number;
  days?: number;
}): Promise<NearbyDuplicate[]> {
  const radius = opts.radiusKm ?? 0.3;
  const days = opts.days ?? 30;
  const dLat = radius / 111;
  const dLng = radius / (111 * Math.max(Math.cos((opts.lat * Math.PI) / 180), 0.01));
  const since = new Date(Date.now() - days * 86400000).toISOString();

  const { data } = await supabase
    .from("posts")
    .select("id, title, category, status, created_at, latitude, longitude")
    .eq("category", opts.category as any)
    .gte("created_at", since)
    .gte("latitude", opts.lat - dLat)
    .lte("latitude", opts.lat + dLat)
    .gte("longitude", opts.lng - dLng)
    .lte("longitude", opts.lng + dLng)
    .limit(20);

  const rows = (data ?? []).filter((p: any) => p.latitude != null && p.longitude != null);
  if (!rows.length) return [];

  const ids = rows.map((r: any) => r.id);
  const { data: verifies } = await supabase
    .from("reactions")
    .select("post_id")
    .eq("reaction", "verify" as any)
    .in("post_id", ids);
  const vMap = new Map<string, number>();
  (verifies ?? []).forEach((v: any) => vMap.set(v.post_id, (vMap.get(v.post_id) ?? 0) + 1));

  return rows
    .map((p: any) => ({
      id: p.id,
      title: p.title,
      category: p.category,
      status: p.status,
      created_at: p.created_at,
      verify_count: vMap.get(p.id) ?? 0,
      distance_km: haversineKm({ lat: opts.lat, lng: opts.lng }, { lat: p.latitude, lng: p.longitude }),
    }))
    .filter((p) => p.distance_km <= radius)
    .sort((a, b) => a.distance_km - b.distance_km)
    .slice(0, 5);
}

/** "I see this too" — toggles a verify reaction on a report. */
export async function toggleConfirmReport(postId: string, userId: string, on: boolean) {
  if (on) {
    const { error } = await supabase
      .from("reactions")
      .insert({ post_id: postId, user_id: userId, reaction: "verify" as any });
    if (error && !error.message.includes("duplicate")) throw error;
  } else {
    const { error } = await supabase
      .from("reactions")
      .delete()
      .eq("post_id", postId)
      .eq("user_id", userId)
      .eq("reaction", "verify" as any);
    if (error) throw error;
  }
}

export async function fetchReportAnalysis(postId: string) {
  const { data } = await supabase
    .from("report_analyses")
    .select("*")
    .eq("post_id", postId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  return data;
}

export async function fetchStatusEvents(postId: string) {
  const { data } = await supabase
    .from("report_status_events")
    .select("id, status, note, proof_url, created_at, changed_by")
    .eq("post_id", postId)
    .order("created_at", { ascending: true });
  return data ?? [];
}

export async function setReportStatus(opts: {
  postId: string;
  status: ReportStatus;
  note?: string;
  proofUrl?: string | null;
  userId: string;
}) {
  const { error } = await supabase.from("posts").update({ status: opts.status as any }).eq("id", opts.postId);
  if (error) throw error;
  const { error: e2 } = await supabase.from("report_status_events").insert({
    post_id: opts.postId,
    status: opts.status as any,
    note: opts.note ?? null,
    proof_url: opts.proofUrl ?? null,
    changed_by: opts.userId,
  });
  if (e2) throw e2;
}

/** Priority score: severity weight × confirmations × recency decay, boosted for clustered reports. */
export function priorityScore(p: {
  severity?: string | null;
  verify_count?: number | null;
  comment_count?: number | null;
  cluster_id?: string | null;
  created_at: string;
}) {
  const sev = { info: 1, warning: 2.5, urgent: 4, critical: 6 }[(p.severity ?? "info") as Severity] ?? 1;
  const confirms = 1 + (p.verify_count ?? 0) * 0.6 + (p.comment_count ?? 0) * 0.15;
  const ageDays = (Date.now() - new Date(p.created_at).getTime()) / 86400000;
  const recency = 1 / (1 + ageDays / 7);
  const clusterBoost = p.cluster_id ? 1.5 : 1;
  return Math.round(sev * confirms * (0.5 + recency) * clusterBoost * 10);
}
